import { trackSpeakingSessionStarted } from "@/lib/analytics/gtm-datalayer";
import type { GuestSpatiusAvatarExperimentProperties } from "@/lib/experiments/guestSpatiusAvatar";
import type { WebappSignupBeforeDemoAttributionProperties } from "@/lib/experiments/webappSignupBeforeDemo";
import { syncAuthenticatedSessionStart } from "@/lib/lifecycle/session-start-client";
import { getAvatarTelemetry } from "@/lib/livekit-agent-telemetry";
import type { SessionMode } from "@/lib/sessionRouting";

type SessionStartLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
};

type PostHogLike = {
  setPersonProperties: (properties: Record<string, unknown>) => void;
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
};

type FetchLike = typeof fetch;

export type RecordSuccessfulSessionConnectAnalyticsInput = {
  mode: SessionMode;
  userType: "authenticated" | "guest";
  selectedLanguageCode: string;
  selectedLanguageName: string;
  selectedLanguageLevel: string;
  selectedVoice: string;
  avatarId?: string;
  noAvatar: boolean;
  instructionLanguageCode?: string | null;
  instructionLanguageName?: string | null;
  roomName?: string | null;
  participantToken?: string | null;
  traceId?: string | null;
  spatiusPrimary: boolean;
  spatiusAudioFallbackActive: boolean;
  tokenSource?: string | null;
  whiteboardEnabled: boolean;
  analyticsSurface?: string;
  experimentProperties?: GuestSpatiusAvatarExperimentProperties;
  webappSignupBeforeDemoExperimentProperties?:
    | WebappSignupBeforeDemoAttributionProperties
    | Record<string, never>;
  guestTotalUsageSeconds: number;
  maxGuestSeconds: number;
  authenticatedUserId?: string | null;
  authenticatedUserCreatedToday: boolean;
  guestSecondsUsedToday: number;
  hasTrackedGuestAuthenticatedSessionStarted: boolean;
  markGuestAuthenticatedSessionStarted: () => void;
  posthogClient?: PostHogLike | null;
  hasPosthogKey: boolean;
  fetchFn?: FetchLike;
  logger: SessionStartLogger;
};

export function recordSuccessfulSessionConnectAnalytics({
  mode,
  userType,
  selectedLanguageCode,
  selectedLanguageName,
  selectedLanguageLevel,
  selectedVoice,
  avatarId,
  noAvatar,
  instructionLanguageCode,
  instructionLanguageName,
  roomName,
  participantToken,
  traceId,
  spatiusPrimary,
  spatiusAudioFallbackActive,
  tokenSource,
  whiteboardEnabled,
  analyticsSurface,
  experimentProperties,
  webappSignupBeforeDemoExperimentProperties,
  guestTotalUsageSeconds,
  maxGuestSeconds,
  authenticatedUserId,
  authenticatedUserCreatedToday,
  guestSecondsUsedToday,
  hasTrackedGuestAuthenticatedSessionStarted,
  markGuestAuthenticatedSessionStarted,
  posthogClient,
  hasPosthogKey,
  fetchFn = fetch,
  logger,
}: RecordSuccessfulSessionConnectAnalyticsInput) {
  trackSpeakingSessionStarted({
    sessionMode: mode,
    userType,
    selectedLanguageCode,
    selectedLanguageName,
    selectedLanguageLevel,
  });
  logger.info("Speaking session started GA4 event tracked");

  if (posthogClient && hasPosthogKey) {
    const avatarTelemetry = getAvatarTelemetry({ selectedVoice, avatarId, noAvatar });
    const webappExperimentProperties = webappSignupBeforeDemoExperimentProperties || {};
    posthogClient.setPersonProperties({
      language_code: selectedLanguageCode,
      language_name: selectedLanguageName,
      language_level: selectedLanguageLevel,
      last_session_mode: mode,
      instruction_language_code: instructionLanguageCode || null,
      instruction_language_name: instructionLanguageName || null,
      last_avatar_name: avatarTelemetry.avatar_name,
      last_avatar_voice: avatarTelemetry.avatar_voice,
    });
    posthogClient.capture("livekit_connected", {
      user_type: userType,
      selected_language_code: selectedLanguageCode,
      selected_language_name: selectedLanguageName,
      selected_language_level: selectedLanguageLevel,
      session_mode: mode,
      room_name: roomName || null,
      trace_id: traceId || null,
      spatius_primary: spatiusPrimary,
      spatius_audio_fallback_active: spatiusAudioFallbackActive,
      token_source: tokenSource || null,
      ...(experimentProperties || {}),
      ...webappExperimentProperties,
      ...avatarTelemetry,
    });
    posthogClient.capture("session_created", {
      user_type: userType,
      selected_language_code: selectedLanguageCode,
      selected_language_name: selectedLanguageName,
      selected_language_level: selectedLanguageLevel,
      session_mode: mode,
      whiteboard_enabled: whiteboardEnabled,
      room_name: roomName || null,
      spatius_primary: spatiusPrimary,
      spatius_audio_fallback_active: spatiusAudioFallbackActive,
      ...(experimentProperties || {}),
      ...webappExperimentProperties,
      ...avatarTelemetry,
    });

    if (userType === "guest" && analyticsSurface === "fullscreen") {
      posthogClient.capture("fullscreen_guest_demo_connected", {
        session_mode: mode,
        selected_language_code: selectedLanguageCode,
        selected_language_name: selectedLanguageName,
        selected_language_level: selectedLanguageLevel,
        guest_seconds_used_total: guestTotalUsageSeconds,
        guest_seconds_remaining_total: Math.max(0, maxGuestSeconds - guestTotalUsageSeconds),
        ...(experimentProperties || {}),
      });
    }

    if (webappExperimentProperties.webapp_signup_before_demo_variant) {
      posthogClient.capture("webapp_signup_before_demo_demo_connected", {
        user_type: userType,
        session_mode: mode,
        selected_language_code: selectedLanguageCode,
        selected_language_name: selectedLanguageName,
        selected_language_level: selectedLanguageLevel,
        ...webappExperimentProperties,
      });
    }

    if (
      userType === "authenticated" &&
      analyticsSurface === "fullscreen" &&
      authenticatedUserId &&
      authenticatedUserCreatedToday &&
      guestSecondsUsedToday > 0 &&
      !hasTrackedGuestAuthenticatedSessionStarted
    ) {
      markGuestAuthenticatedSessionStarted();
      posthogClient.capture("fullscreen_guest_authenticated_session_started", {
        user_id: authenticatedUserId,
        session_mode: mode,
        selected_language_code: selectedLanguageCode,
        selected_language_name: selectedLanguageName,
        selected_language_level: selectedLanguageLevel,
        guest_seconds_used_today: guestSecondsUsedToday,
      });
    }
  }

  if (userType === "authenticated" && authenticatedUserId) {
    return syncAuthenticatedSessionStart({
      userId: authenticatedUserId,
      fetchFn,
      logger,
      payload: {
        selectedLanguageCode,
        selectedLanguageName,
        selectedLanguageLevel,
        sessionMode: mode,
        instructionLanguageCode: instructionLanguageCode || null,
        instructionLanguageName: instructionLanguageName || null,
        roomName: roomName || null,
        participantToken: participantToken || null,
      },
    });
  }
}
